<template>
  <Loader :value="initPromise" :message="$metaInfo.title" class="post-mutate" :class="{ 'post-mutate--new': isNew }">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <div class="row g-1 wrap">


            <ButtonComponent v-if="item.url && item.isPublished" tag="a" :href="item.url" target="_blank" :label="$t('preview')" :icon="['fal', 'eye']" />


            <Actions placement="post" :query="{ post: id }">
              <template #bottom>
                <ButtonComponent
                  v-if="!isNew"
                  v-tooltip.right="{
                    content: !$path('metadata.canRemove', item) ? $t('cannotDelete') : undefined,
                    boundariesElement: body,
                  }"
                  :label="$t('delete')"
                  :class="{ 'elder-button--disabled': !$path('metadata.canRemove', item) }"
                  @click="onRemoveClick"
                  :icon="['fal', 'times']"
                />
              </template>
            </Actions>

            <ButtonComponent
              :label="saveState.label"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', saveState.icon]"
              theme="primary"
              :promise="promises.mutate"
              :class="{
                'elder-button--disabled': saveState.disabled,
                'elder-button--invalid': saveState.invalid,
              }"
              v-tooltip="{ content: saveState.tooltip }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <Card tag="form" @submit.prevent="submit" class="post-mutate__form">
        <template #default>
          <div class="post-mutate__inputs">
            <MediaComponent
              v-model="data.media.cover"
              multiple
              :types="types"
              :label="labels.cover"
              :upload-options="{ compression: 'gallery' }"
              placement="inside"
              data-field="cover"
              class="post-mutate__inputs-cover"
            />

            <InputComponent
              v-model="data.title"
              data-field="title"
              :label="labels.title"
              required
              :is-valid="validator.fields.title.valid"
              :validation-message="validator.fields.title.error"
            />

            <InputComponent :value="data.content" data-field="content" :label="labels.content">
              <RichTextComponent v-model="data.content" />
            </InputComponent>

            <InputComponent
              v-if="!isNew"
              v-model="data.slug"
              data-field="slug"
              :label="labels.slug"
              required
              :is-valid="validator.fields.slug.valid"
              :validation-message="validator.fields.slug.error"
            />

            <InputComponent :value="publishedFrom" :label="labels.publishedFrom" data-field="publishedFrom">
              <InputDate v-model="publishedFrom" />
            </InputComponent>

            <InputComponent v-if="!isNew" :label="labels.author" data-field="author">
              <UserSelect
                v-model="data.author"
                search
                :items="fetchUsers"
                :title="$t('selectx', { x: labels.author.toLowerCase() })"
                class="elder-input__element"
            /></InputComponent>

            <CustomFields
              v-if="customFieldSchemas.length"
              v-model="data.customFields"
              data-field="customFields"
              :data="data"
              :schema="customFieldSchemas"
              :validator="validator"
              validator-prefix="customFields"
              @remove="$ev => removeCustomField($ev)"
            />
          </div>
        </template>
      </Card>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push(
            returnTo || {
              name: 'dashboard',
            },
          )
        "
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import Model from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import { assocPath, mergeDeepRight } from 'ramda'
import { DateTransformer } from '@/utils/transformers'
import { ToArray } from '@/utils'
import { getMediaTypes } from '@kvass/media-types'
import UserApi from '@/api/User'
import graphql from '@/api/graphql'
import MetadataQuery from '@/api/graphql/queries/PostMutateMetadata.gql'

import UserSelect from '@/components/User/Select/Modal'
import { InputComponent } from 'vue-elder-input'
import { MediaComponent } from '@kvass/media'
import { DropdownComponent } from 'vue-elder-dropdown'
import RichTextComponent from '@kvass/rich-text'
import InputDate from '@/components/InputDate'
import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'
import RemoveComponent from '@/components/Remove'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import Actions from '@/components/Actions'

let getLabel = (label, count = 1) => (label || '').split('|')[count > 1 ? 'pop' : 'shift']()

export default {
  mixins: [
    Validator({
      data: 'data',
      labels: 'labels',
      rules: 'rules',
    }),
    Model({
      source: 'item',
      model: {
        id: null,
        slug: null,
        title: null,
        content: null,
        media: {
          cover: null,
        },
        author: null,
        customFields: null,
        customFieldsConfig: [],
        publishedFrom: null,
      },
      transform: {
        author: v => (v && v.id) || null,
        'customFieldsConfig.*.schema': undefined,
      },
      guard: true,
    }),
  ],
  props: {
    id: String,
    reference: Object,
    collection: String,
    config: String,
    returnTo: String,
  },
  data() {
    return {
      metadata: null,
      removeDialog: false,
      types: getMediaTypes(),
      initPromise: null,
      body: document.body,
    }
  },
  watch: {
    id: {
      handler(id) {
        this.initPromise = this.init(id)
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Post', ['item', 'promises', 'states']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
    }),
    labels() {
      return {
        ...mapi18n(['title', 'author', 'content', 'publishedFrom', 'slug', 'cover']),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    rules() {
      return {
        title: 'required|string',
        slug: 'slug',
        ...this.customFieldValidatorConfig.rules,
      }
    },
    publishedFrom: DateTransformer('data.publishedFrom'),
    isNew() {
      return !this.id
    },
    metadataLabels() {
      return {
        collection: this.$path('metadata.PostCollection.label'),
        config: (
          (this.$path('metadata.PostCollection.schemas') || []).find(({ source }) => source === this.config) || {}
        ).label,
      }
    },
    breadcrumbs() {
      return [
        {
          title: this.$tc('post', 2),
          condition: !this.reference,
        },
        {
          title: getLabel(this.metadataLabels.collection, 2),
          action: {
            name: `${this.reference.onModel.toLowerCase()}-posts`,
            params: { id: this.reference.ref },
            query: { collection: this.collection },
          },
          condition: this.$path('metadata.PostCollection'),
        },
        { title: this.$metaInfo.title },
      ]
        .flat()
        .filter(Boolean)
        .filter(e => !('condition' in e) || e.condition)
    },
    customFieldSchemas() {
      if (!this.data) return []
      return this.data.customFieldsConfig.map(c => c.schema).flat()
    },
    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },
    saveState() {
      let base = {
        label: this.isNew ? this.$t('create') : this.$t('save'),
        icon: this.isNew ? 'plus' : 'arrow-to-bottom',
        disabled: !this.validator.passes || !this.hasChanges,
      }

      if (!this.states.fetchOne && !this.validator.passes)
        return {
          ...base,
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('\n'),
        }

      if (!this.hasChanges)
        return {
          ...base,
          tooltip: this.$t('noUnsavedChanges'),
        }

      return base
    },
  },
  methods: {
    ...mapActions('Post', {
      fetch(dispatch) {
        if (!this.id) {
          const boilerplate = {
            customFieldsConfig: this.metadata.CustomFieldSchemas.filter(s => ToArray(this.config).includes(s.id)).map(
              s => ({
                source: s.id,
                schema: s.fields,
              }),
            ),
            customFields: this.metadata.CustomFieldSchemas.reduce((res, cur) => ({ ...res, ...cur.default }), {}),
          }

          return this.set(boilerplate)
        }
        return dispatch('fetch', { id: this.id })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),
    ...mapMutations('Post', ['set']),
    onRemoveClick() {
      if (!this.$path('item.metadata.canRemove')) return
      this.removeDialog = true
    },
    submit() {
      let data = {
        id: this.id,
        ...this.getData({ diff: !this.isNew }),
      }

      if (this.isNew && this.reference) data.references = [this.reference]

      this.mutate([data, { strategy: (item, { id }) => mergeDeepRight(item, { ...this.data, id }) }]).then(data => {
        if (!this.isNew) return

        this.hasChanges = false // Change detector is to slow - forcing no change
        if (this.returnTo) return this.$router.push(this.returnTo)
        return this.$router.replace({
          name: 'post-edit',
          params: { id: data.id },
          query: this.$route.query,
        })
      })
    },
    async init(id) {
      await this.getMetadata()
      await this.fetch()
    },
    fetchUsers(filter) {
      return UserApi.get({ filter }, { query: 'UserSearch' })
    },
    getMetadata() {
      let getCollection = Boolean(this.collection),
        getCustomFieldSchemas = Boolean(this.config)
      if (!getCollection && !getCustomFieldSchemas) return Promise.resolve()

      return graphql
        .request(MetadataQuery, {
          getCollection,
          collection: this.collection,
          getCustomFieldSchemas,
          customFieldSchemas: ToArray(this.config),
        })
        .then(metadata => (this.metadata = metadata))
    },
    removeCustomField(item) {
      this.data.customFields = assocPath(item.key.split('.'), null, this.data.customFields)
      this.data.customFieldsConfig = this.data.customFieldsConfig.filter(c => c.source !== item.source)
    },
  },
  metaInfo() {
    return {
      title: this.isNew
        ? this.$t('createResource', {
            resource: (getLabel(this.metadataLabels.config) || this.$tc('post')).toLowerCase(),
          })
        : this.item.title || this.i18n.loading,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    RemoveComponent,
    Card,
    UserSelect,
    MediaComponent,
    RichTextComponent,
    CustomFields,
    InputDate,
    Actions,
  },
}
</script>

<style lang="scss">
.post-mutate {
  background-color: $background-grey;

  &__form {
    max-width: 1200px;
    margin: 0 auto;
  }

  &__inputs {
    display: grid;

    grid-template-columns: 1fr 1fr 1fr 1fr;

    grid-template-areas:
      'title title cover cover'
      'content content cover cover'
      'content content publishedFrom slug'
      'content content author author'
      'customFields customFields customFields customFields';

    grid-template-rows: auto auto auto 1fr;
    grid-auto-rows: auto;
    grid-gap: $form-gap-row / 2 $form-gap-column;

    align-items: start;

    &-cover {
      width: 100%;

      .kvass-media-droparea-wrapper {
        display: flex;
        flex-direction: column;
      }
    }
  }

  $fields: cover, title, slug, customFields, publishedFrom, author, content;

  @each $field in $fields {
    [data-field='#{$field}'] {
      grid-area: $field;
    }
  }

  [data-field='cover'] {
    aspect-ratio: 6 / 4;
    margin-bottom: 0.5rem;
  }

  [data-field='content'] {
    align-self: stretch;
  }

  &--new {
    .post-mutate__inputs {
      grid-template-areas:
        'title title cover cover'
        'content content cover cover'
        'content content publishedFrom publishedFrom'
        'customFields customFields customFields customFields';
      grid-template-rows: auto auto 1fr;
    }
  }

  .custom-fields {
    margin-top: 0;
  }

  [type='submit'] {
    margin-right: auto;
  }
}
</style>
